import {
  Button,
  LoadingSpinner,
  MenuListSelect,
  Modal,
  Input,
} from '@rabbit/elements/shared-components';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryParams } from 'raviger';
import { SearchVendablesBySkuCode } from '@rabbit/search/cherchons';
import { DTVendable } from '@rabbit/data/types';
import { useTranslation } from 'react-i18next';
import ROUTE_NAME from '@rabbit/olive/utils/url-constants.tsx';
import { UserContext } from '@rabbit/olive/context/UserContext.tsx';
import { BL_Tech } from '@rabbit/bizproc/core';
import { SelectChangeEvent } from '@mui/material';
import { AVAILABLE_LANGUAGE_CODES } from '@rabbit/sage/utils/consts.ts';
import { AppContext } from '@rabbit/app-context';
import { Form, Formik } from 'formik';

export default function RegisterView() {
  const navigate = useNavigate();
  const { consumerPersonaData } = useContext(UserContext) || {};
  const params = useQueryParams()[0];
  const sku = params?.sku;
  const sn = params?.sn ?? false;
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [vendableData, setVendableData] = useState<DTVendable | null>(null);
  const UPC_PREFIX = t('CFG_COBRAND_UPC_PREFIX', { defaultValue: '' });
  const [lang, setLang] = useState<string>(
    window.localStorage.getItem('lang') ?? 'en'
  );
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginOption, setLoginOption] = useState<'login' | 'withoutLogin'>(
    'login'
  );

  const { config } = useContext(AppContext);

  const cobrand = t('CFG_COBRAND_LOGO');
  useEffect(() => {
    if (sku) {
      (async () => {
        const res = await SearchVendablesBySkuCode(`${UPC_PREFIX}${sku}`);
        if (res) setVendableData(res?.items[0]);
      })()
        .catch((err) => console.log(err))
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [sku]);
  const productPlaceHolder = t(
    'CFG_COBRAND_LANDING_IMAGE_PRODUCT_PLACEHOLDER',
    { defaultValue: '' }
  );
  const defaultImage =
    t('CFG_COBRAND_LANDING_IMAGE_PRODUCT_PLACEHOLDER') || productPlaceHolder;
  const onClickRegisterProduct = () => {
    if (!vendableData) {
      navigate(
        consumerPersonaData ? ROUTE_NAME.PRODUCTS_SEARCH : ROUTE_NAME.AUTH
      );
      return;
    }

    if (sn) {
      window.localStorage.setItem('LRP_VendableSn', sn);
    }

    const path = consumerPersonaData
      ? `${ROUTE_NAME.PRODUCTS_PREVIEW}/${vendableData.docid}/register`
      : ROUTE_NAME.AUTH;

    if (!consumerPersonaData) {
      window.localStorage.setItem('LRP_VendableId', vendableData.docid);
      window.localStorage.setItem('LRP_VendableName', vendableData.title);
    }

    navigate(path);
  };
  const handleShowLoginModal = () => {
    setShowLoginModal(true);
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginOptionSubmit = () => {
    if (loginOption === 'login') {
      onClickSageProduct();
    } else {
      onClickSageProductWithoutLogin();
    }
    setShowLoginModal(false);
  };

  const onClickSageProduct = () => {
    const host = `${window.location.protocol}//${window.location.host}`;
    const url = BL_Tech.getSageUrlFromOlive(host);
    const queryParams = new URLSearchParams();

    if (sku) queryParams.append('sku', sku);
    if (sn) queryParams.append('sn', sn);

    queryParams.append('lang', lang);
    window.location.replace(`${url}/login?${queryParams.toString()}`);
  };

  const onClickSageProductWithoutLogin = () => {
    const host = `${window.location.protocol}//${window.location.host}`;
    const url = BL_Tech.getSageUrlFromOlive(host);
    const queryParams = new URLSearchParams();

    if (sku) queryParams.append('sku', sku);
    if (sn) queryParams.append('sn', sn);

    queryParams.append('lang', lang);
    window.location.replace(
      `${url}/register-product?${queryParams.toString()}`
    );
  };

  const handleChange = async (event: SelectChangeEvent) => {
    setLang(event.target.value as string);
    window.localStorage.setItem('lang', event.target.value as string);
    await i18n.changeLanguage(event.target.value as string);
  };
  const availableLanguages = AVAILABLE_LANGUAGE_CODES.map((data) => ({
    value: data.code,
    label: config.ACCOUNT.LANGUAGE_LABEL ? data.language : data.code,
  }));

  // Login Modal
  const renderLoginModal = () => {
    return (
      <Modal
        kind="generic"
        settings={{
          title: t('message.registerProductAsInstaller'),
          primaryButtonText: t('general.continue'),
          outlineButtonText: t('general.cancel'),
          handleClose: handleCloseLoginModal,
          handlePrimaryClick: handleLoginOptionSubmit,
          handleOutlineClick: handleCloseLoginModal,
        }}
      >
        <div className="font-nunito p-3 sm:p-4">
          <p className="font-nunito mb-3 text-center text-sm sm:mb-4 sm:text-base">
            {t('message.pleaseSelectAnOption')}
          </p>
          <Formik initialValues={{ loginOption: 'login' }} onSubmit={() => {}}>
            <Form>
              <div className="mb-3 sm:mb-4">
                <Input
                  type="radio"
                  name="loginOption"
                  onClick={() => setLoginOption('login')}
                  settings={{
                    radioValue: 'login',
                    radioLabel: t('message.Ihaveaactiveaccount'),
                    radioLabelStyles: 'font-nunito text-sm sm:text-base',
                    values: { loginOption },
                  }}
                />
              </div>
              <div className="mb-3 sm:mb-4">
                <Input
                  type="radio"
                  name="loginOption"
                  onClick={() => setLoginOption('withoutLogin')}
                  settings={{
                    radioValue: 'withoutLogin',
                    radioLabel: t('message.dontHave'),
                    radioLabelStyles: 'font-nunito text-sm sm:text-base',
                    values: { loginOption },
                  }}
                />
              </div>
            </Form>
          </Formik>
        </div>
      </Modal>
    );
  };

  return (
    <div className="bg-canvas flex grow flex-col lg:content-center">
      <div
        className={`relative mx-auto w-full max-w-full px-4 sm:px-6 md:m-auto md:grid md:w-[95%] md:max-w-[480px] md:grid-cols-1 md:gap-12`}
      >
        <div
          className={`relative rounded-[6px] bg-white px-4 py-8 sm:px-6 sm:py-10 md:px-16 md:py-12 md:shadow-[4px_6px_24px_0px_rgba(0,0,0,0.08)]`}
        >
          {isLoading && <LoadingSpinner size="sm" overlay={true} />}
          {!isLoading && (
            <div className="flex flex-col">
              <div className="mb-10 flex justify-end">
                <MenuListSelect
                  label="lang"
                  value={lang}
                  handleChange={handleChange}
                  menuItem={availableLanguages}
                />
              </div>
              <img
                alt=""
                src={cobrand}
                className="mb-4 max-h-[50px] w-full object-contain sm:max-h-[60px] md:max-h-[72px]"
              />
              <div className={`flex flex-col ${vendableData ? 'gap-6' : ''}`}>
                <div>
                  <div
                    className={`flex items-center justify-center ${
                      vendableData
                        ? 'min-h-[200px] sm:min-h-[250px] md:min-h-[300px]'
                        : 'min-h-[220px] sm:min-h-[270px] md:min-h-[320px]'
                    } w-full py-4`}
                  >
                    <img
                      alt=""
                      src={
                        vendableData
                          ? vendableData.img && vendableData.img.length > 0
                            ? vendableData.img[0]
                            : defaultImage
                          : defaultImage
                      }
                      className="max-h-[180px] w-auto object-contain sm:max-h-[230px] md:max-h-[280px]"
                    />
                  </div>
                  {vendableData && (
                    <div className="mt-4 px-2 text-center">
                      <h2 className="font-nunito break-words text-xl font-medium text-gray-800 sm:text-2xl">
                        {vendableData.title}
                      </h2>
                      {vendableData.mpn && (
                        <p className="font-nunito mt-1 text-xs text-gray-500 sm:text-sm">
                          {vendableData.mpn}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex w-full flex-col items-center gap-3 px-2 sm:px-4">
                  <Button
                    onClick={handleShowLoginModal}
                    className="w-full max-w-full px-4 py-2 sm:max-w-[280px] sm:px-6"
                    kind="primary"
                    size="sm"
                  >
                    {t('message.registerProductAsInstaller')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={onClickRegisterProduct}
                    className="w-full max-w-full px-4 py-2 sm:max-w-[280px] sm:px-6"
                    kind="outline"
                  >
                    {t('message.registerProductAsConsumer')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showLoginModal && renderLoginModal()}
    </div>
  );
}
